import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { Header } from '@/components/custom/header/header';
import { Menu } from '@/components/custom/menu';
import CpfModal from '@/components/custom/cpf-modal';
import { useSession } from '@/hooks/useSession';
import PtsModal from '@/pages/users/PtsModal';
import ActionCableConsumer from '@/utils/actionCableConsumer';
import DuelNotification from '@/components/custom/duel-notification';
import NoClassroomModal from '@/components/custom/no-classroom-modal';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { LogosSeduc } from '@/components/custom/logos-seduc/LogosSeduc';
import PersonalDataModal from '@/components/custom/personal-data-modal';
import CpfConfirmationModal from '@/components/custom/cpf-confirmation-modal';
import { Dashboard } from '@/layouts/dashboard';
import { PlayfulNavContext } from '@/lib/playful-nav-context';
import { BottomBarInfantil } from '@/components/custom/bottom-bar-infantil';
import { BottomBarProfessor } from '@/components/custom/bottom-bar-professor';
import usePlayfulNav from '@/hooks/usePlayfulNav';
import { isPlayfulTheme } from '@/lib/layout-version';
import { TiaChatWidget } from '@/components/custom/tia-chat-widget';

const pageLayoutsWrapperVariants = cva('c-page-layouts-wrapper w-full mt-0', {
  variants: {
    hasCustomLogos: {
      true: 'mt-[68px] md:mt-0',
      false: 'mt-0'
    }
  },
  defaultVariants: {
    hasCustomLogos: false
  }
});

const PageLayoutsWrapper = React.forwardRef(({ className, hasCustomLogos, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(pageLayoutsWrapperVariants({ hasCustomLogos }), className)}
      {...props}
    />
  );
});
PageLayoutsWrapper.displayName = 'PageLayoutsWrapper';

export function PageLayouts(props) {
  const { layoutType, children } = props;
  const [showModal, setShowModal] = React.useState(false);
  const { session } = useSession();
  const [achievementData, setAchievementData] = React.useState(null);
  const [duelData, setDuelData] = React.useState(null);
  const [showPersonalDataModal, setShowPersonalDataModal] = React.useState(false);
  const [showCpfConfirmationModal, setShowCpfConfirmationModal] = React.useState(false);

  const reviwedData = session?.user?.reviewedInfos;

  //noclassroom modal
  const client = useClient();
  const currentUser = session?.user;
  const [show, setShow] = React.useState(false);
  const [studentClassrooms, setStudentClassrooms] = React.useState();
  const [verifiedUser, setVerifiedUser] = React.useState(null);

  const renderCPF = currentUser?.cpfCnpj === null || currentUser?.cpfCnpj === '';
  const renderClassrooms = studentClassrooms?.length === 0 || studentClassrooms?.some(e => ['turma única', 'sem turma', 'sem escola'].includes(e.title.toLowerCase()) && !['sem escola', 'já sou formado'].includes(e.institution.name.toLowerCase()));

  const appName = process.env.REACT_APP_NAME;
  const allowCreateUser = process.env.REACT_APP_ALLOW_CREATE_USER === 'true';
  const hasCustomLogos = appName === 'canaleducacao' || appName === 'maiseducacao';
  const isMainLayout = layoutType === 'mainLayout';

  // Quem troca a sidebar por bottom bar (aluno e professor) vs quem fica na
  // sidebar (admin, tutor, secretariado): regra única no usePlayfulNav — o
  // perfil consulta o mesmo hook para não deixar o professor sem logout.
  const playfulNav = usePlayfulNav();
  const isStudent = currentUser?.profileNames?.[0] === 'Aluno';

  React.useEffect(() => {
    const openModal = !reviwedData && currentUser?.profileNames[0] === 'Aluno' && (renderCPF || renderClassrooms) && !allowCreateUser;
    setShow(openModal);
  }, [studentClassrooms, currentUser, allowCreateUser]);

  async function getStudentClassroom() {
    const { data, error } = await client.fetch('classrooms');
    if (error) {
      toast.error('Erro ao buscar turmas');
    } else {
      setStudentClassrooms(data);
    }
  }

  async function checkVerifiedUser() {
    const { data, error } = await client.fetch(`users/${currentUser.id}/verifications`);
    if (error) {
      toast.error('Erro ao validar dados do usuário');
    } else {
      setVerifiedUser(data);
    }
  }

  React.useEffect(() => {
    if (session) {
      if (currentUser?.profileNames[0] === 'Aluno' || currentUser?.profileNames[0] === 'Professor') {
        checkVerifiedUser();
      }
      if (currentUser?.profileNames[0] === 'Aluno') {
        getStudentClassroom();
      }
    }
  }, [session]);

  React.useEffect(() => {
    if (appName === 'maiseducacao' && currentUser?.profileNames[0] === 'Aluno' && verifiedUser?.['cpf-verified'] === false) {
      setShowCpfConfirmationModal(true);
    }

    if (currentUser?.profileNames[0] === 'Aluno' && verifiedUser?.['cpf-verified'] === false && appName === 'canaleducacao') {
      setShowPersonalDataModal(true);
    }
  }, [verifiedUser]);

  return (
    <PlayfulNavContext.Provider value={playfulNav}>
      <Dashboard>
        {isMainLayout && session && (
          <>
            {/* Na navegação lúdica o logo institucional já vive dentro do header
                (playfulLogo é o mesmo arquivo) — o banner duplicaria a marca. */}
            {!playfulNav && <LogosSeduc hasCustomLogos={hasCustomLogos} />}
            {!playfulNav && <Menu hasCustomLogos={hasCustomLogos} />}
            {/* Mesma barra, destinos diferentes: aluno tem lista própria
                (lúdica), professor projeta o adminMenu da sidebar. O else só é
                "professor" por eliminação (playfulNav ⇒ aluno ou professor), o
                que acopla este ponto ao hook: se um terceiro perfil lúdico
                entrar lá, ele cairia no adminMenu sem passar por aqui. O
                !currentUser mantém o predicado original e deixa a eliminação
                explícita — dentro deste bloco session.user sempre existe: ele
                só monta sob `session &&`, e os ActionCableConsumer abaixo leem
                session.user.* sem guarda. */}
            {playfulNav && (!currentUser || isStudent ? <BottomBarInfantil /> : <BottomBarProfessor />)}
            {!isPlayfulTheme() && <TiaChatWidget />}
            <Header
              hasCustomLogos={hasCustomLogos}
              playfulNav={playfulNav}
            />
            <NoClassroomModal
              renderCPF={renderCPF}
              renderClassrooms={renderClassrooms}
              show={show}
              setShow={setShow}
              currentUser={currentUser}
            />
            {showPersonalDataModal && (
              <PersonalDataModal
                show={showPersonalDataModal}
                setShow={setShowPersonalDataModal}
                currentUser={currentUser}
                verifiedUser={verifiedUser}
              />
            )}
            <CpfConfirmationModal
              showModal={showCpfConfirmationModal}
              setShowModal={setShowCpfConfirmationModal}
              currentUser={currentUser}
            />
            <CpfModal
              showModal={showModal}
              setShowModal={setShowModal}
            />
            <PtsModal
              achievementData={achievementData}
              setAchievementData={setAchievementData}
            />
            <DuelNotification
              duelData={duelData}
              setDuelData={setDuelData}
            />
            <ActionCableConsumer
              channel="UserMilestoneChannel"
              setObjectChannel={setAchievementData}
              channelKey={'user_id'}
              channelId={session.user.id}
            />
            <ActionCableConsumer
              channel="DuelNotificationsChannel"
              setObjectChannel={setDuelData}
              token={session.user.authToken}
            />
          </>
        )}

        {/* Sem banner no playful → sem mt-[68px] compensatório no mobile. */}
        <PageLayoutsWrapper hasCustomLogos={hasCustomLogos && isMainLayout && !playfulNav}>{children}</PageLayoutsWrapper>
      </Dashboard>
    </PlayfulNavContext.Provider>
  );
}
